import {
  DEFAULT_CAMERA_INSPECTION_DATA,
  generateDeviceData,
  generateStreamSettingsData,
  generateStreamStability,
  getStreamStabilitySnapshotHash,
} from 'obfuscated/inquiry/lib/utils/camera-inspection';
import { useRef } from 'react';

import { captureException } from 'lib/reporting';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';

const IMAGE_QUALITY = 0.92;

export interface UseCameraInspectionReturn {
  logCameraInspectionSnapshot: (canvas?: HTMLCanvasElement) => void;
  logCameraInspectionFromStream: (stream?: MediaStream) => Promise<void>;
  getCameraInspectionData: (options: { includeSmallImages: boolean }) => CameraInspectionData;
}

export const useCameraInspection = (): UseCameraInspectionReturn => {
  /**
   * An array of hashes of images taken from the video feed to determine stream stability.
   * We hash the images because storing multiple images in memory can lead to OOMs.
   *
   * use a ref as this could be accessed via a timeout
   */
  const cameraInspectionSnapshots = useRef<string[]>([]);
  const shouldLogSnapshot = useRef<boolean>(true);
  const cameraInspectionData = useRef<CameraInspectionData>({ ...DEFAULT_CAMERA_INSPECTION_DATA });

  return {
    logCameraInspectionSnapshot: (canvas?: HTMLCanvasElement): void => {
      if (!canvas) {
        return;
      }

      try {
        const snapshots = cameraInspectionSnapshots.current;
        if (snapshots.length >= 5) return;

        // capture a snapshot every other tick to reduce false rejections due to low-end cameras
        if (shouldLogSnapshot.current) {
          const snapshotHash = getStreamStabilitySnapshotHash(
            canvas?.toDataURL('image/jpeg', IMAGE_QUALITY),
          );

          if (snapshotHash != null) {
            snapshots.push(snapshotHash);
            shouldLogSnapshot.current = false;
          }
        } else {
          shouldLogSnapshot.current = true;
        }
      } catch (e) {
        captureException(e);
      }
    },
    logCameraInspectionFromStream: async (stream?: MediaStream): Promise<void> => {
      // skip collection if we already collected camera data
      if (!stream || cameraInspectionData.current.kind) return;

      /**
       * NB: We do two separate updates on cameraInspectionData.current here because calling
       * `enumerateDevices` can be extremely slow on old/mobile devices. The first update just reads the
       * current stream settings (which is fast) and the second state update calls `enumerateDevices`. This is so
       * that we have a chance to populate CameraInspectionData with the most amount of data possible before
       * the user switches to the next screen. They may switch to the next screen extremely quickly due to fast
       * autocapture (especially on gov id)
       */
      let updatedCameraInspectionData: CameraInspectionData = {
        ...cameraInspectionData.current,
        ...generateStreamSettingsData(stream),
      };

      cameraInspectionData.current = updatedCameraInspectionData;

      updatedCameraInspectionData = {
        ...updatedCameraInspectionData,
        ...(await generateDeviceData(stream)),
      };

      cameraInspectionData.current = updatedCameraInspectionData;
    },
    getCameraInspectionData: ({
      includeSmallImages,
    }: {
      includeSmallImages: boolean;
    }): CameraInspectionData => {
      return {
        ...cameraInspectionData.current,
        streamStability: generateStreamStability({
          snapshots: cameraInspectionSnapshots.current,
          includeSmallImages,
        }),
      };
    },
  };
};
